import {convertImageToWebP} from '@/helpers/shadcn/utils';
import {getFileVerification} from '@/services/fileUploadService';
import {Constants} from '@/utils/constants/commonConstants';
import {request} from '@/helpers/apiClient';
import {TIER_CONFIG} from '@/utils/constants/apiConfig/tierConfig';
import {optimizeImage} from '@/utils/imageOptimization';

/**
 * Extracts CDN URL from the media/upload API response
 */
const extractCdnUrl = (response) => {
  if (!response) return undefined;
  const list = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];
  const first = list[0];
  return (
    first?.media?.cdnUrl ||
    first?.cdnUrl ||
    first?.url ||
    response?.data?.cdnUrl ||
    response?.data?.url
  );
};

/**
 * Legacy upload: optimizes and uploads via tiers/image endpoint (used for Colombia)
 *
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - API response containing imageUploadedRef and optimizedImage
 */
export const uploadTierImageLegacy = async (imageFile) => {
  const optimizedImage = await optimizeImage(imageFile);

  const formData = new FormData();
  formData.append('image', optimizedImage);

  const response = await request(TIER_CONFIG.UPLOAD_TIER_IMAGE, {
    data: formData
  });
  return {
    ...response,
    optimizedImage
  };
};

/**
 * Blob/CDN upload: converts to WebP and uploads via media/upload endpoint
 *
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - Object containing cdnUrl and fileName
 */

/**
 * Uploads an image for tier configuration via blob/CDN
 *
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - Object containing cdnUrl and fileName
 */
export const uploadTierImageBlob = async (imageFile) => {
  const converted = await convertImageToWebP(imageFile, {
    entityType: 'rewards'
  });
  const {webpFile, metadata} = converted;

  const metadataPayload = {
    mediaUploadMetadata: [
      {
        id: metadata.id,
        referenceEntity: metadata.referenceEntity,
        originalFileName: metadata.originalFileName,
        originalFormat: metadata.originalFormat,
        originalSize: metadata.originalSize,
        width: metadata.width,
        height: metadata.height,
        blobFileSize: metadata.blobFileSize,
        blobFormat: metadata.blobFormat
      }
    ]
  };

  const formData = new FormData();
  formData.append('images', webpFile);
  formData.append('metadata', JSON.stringify(metadataPayload));

  const response = await getFileVerification(
    formData,
    Constants.Media,
    undefined,
    'webp'
  );

  const cdnUrl = extractCdnUrl(response);
  if (!cdnUrl) {
    throw new Error('Image upload failed: no CDN URL returned');
  }

  return {
    cdnUrl,
    fileName: imageFile.name
  };
};

/**
 * Uploads an image for tier configuration.
 * Delegates to blob/CDN or legacy upload based on the useBlobUpload flag.
 *
 * @param {File} imageFile - The image file to upload
 * @param {boolean} useBlobUpload - Whether to use blob/CDN upload (true) or legacy (false)
 * @returns {Promise<Object>} - Upload result
 */
export const uploadTierImage = async (imageFile, useBlobUpload = true) => {
  if (useBlobUpload) {
    return uploadTierImageBlob(imageFile);
  }
  return uploadTierImageLegacy(imageFile);
};

/**
 * Reads a file as base64 data URL for immediate display
 *
 * @param {File} file - The file to read
 * @returns {Promise<string>} - Promise resolving to data URL
 */
export const readFileAsDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Converts base64 image data to data URL for display
 *
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} mimeType - MIME type (default: 'image/jpeg')
 * @returns {string} - Data URL for use in img src
 */
export const convertBase64ToDataUrl = (base64Data, mimeType = 'image/jpeg') => {
  if (!base64Data) return null;
  return `data:${mimeType};base64,${base64Data}`;
};

// Simple in-memory cache to store uploaded image CDN URLs and names
// This helps preserve images when switching between language tabs
const imageCache = new Map();

export const cacheImageDataUrl = (imageKey, imageUrl, name) => {
  if (imageKey && imageUrl) {
    imageCache.set(String(imageKey), {dataUrl: imageUrl, name});
  }
};

export const getCachedImageDataUrl = (imageKey) => {
  if (!imageKey) return null;
  // If the key itself is a CDN URL, return it directly
  if (typeof imageKey === 'string' && imageKey.startsWith('http')) {
    return imageKey;
  }
  const {dataUrl} = imageCache.get(String(imageKey)) || {};
  return dataUrl || null;
};

export const getCachedImageName = (imageKey) => {
  if (!imageKey) return null;
  const {name} = imageCache.get(String(imageKey)) || {};
  return name || null;
};
